import React, { ChangeEvent, ReactElement } from 'react';
import { getFormattedDate } from '~utils/date';

export interface WeekSelectProps {
    value?: string;
    label: string;
    nextLabel: string;
    previousLabel: string;
    currentLabel: string;
    id: string;
    onChange: (date: string) => void;
}

const toPaddedString = (value: number): string => {
    return value.toString().padStart(2, '0');
};

const getFormattedDay = (date: Date): string => {
    return `${toPaddedString(date.getDate())}. ${toPaddedString(date.getMonth() + 1)}. ${date.getFullYear()}`;
};

const convertToWeekStart = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() - ((newDate.getDay() + 6) % 7)); // Set to nearest previous Monday
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

const convertToWeekEnd = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + 6 - ((date.getDay() + 6) % 7)); // Set to nearest next Sunday
    newDate.setHours(23, 59, 59, 999);
    return newDate;
};

const getWeeksCount = (date: Date): number => {
    return date.getFullYear() * 53 + getWeek(date);
};

const getWeek = function(input: Date): number {
    const date = new Date(input);
    date.setHours(0, 0, 0, 0);
    // Thursday in current week decides the year.
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    // January 4 is always in week 1.
    const week1 = new Date(date.getFullYear(), 0, 4);
    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const addWeeks = (date: Date, weeks: number): Date => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + weeks * 7);
    return newDate;
};

const getWeekOptions = (startDate?: Date, endDate?: Date, value?: Date): Date[] => {
    const now = convertToWeekStart(new Date());
    startDate = convertToWeekStart(startDate ?? addWeeks(now, -52));
    endDate = convertToWeekEnd(endDate ?? addWeeks(now, 52));
    const rows: Array<Date> = [];
    if (value && getWeeksCount(value) < getWeeksCount(startDate)) {
        rows.push(value);
    }
    let currentDate = new Date(startDate);
    while (getWeeksCount(currentDate) < getWeeksCount(endDate)) {
        rows.push(currentDate);
        currentDate = addWeeks(currentDate, 1);
    }
    if (value && getWeeksCount(value) > getWeeksCount(endDate)) {
        rows.push(value);
    }
    return rows;
};

export default function WeekSelect(props: WeekSelectProps): ReactElement {
    const onChangeWeek = (event: ChangeEvent): void => {
        props.onChange((event.currentTarget as HTMLInputElement).value);
    };

    const onLinkClick = (newValue: Date): void => {
        props.onChange(getFormattedDate(newValue));
    };
    const value = convertToWeekStart(new Date(props.value ?? new Date()));

    const currentValue = convertToWeekStart(new Date());
    const prevValue = convertToWeekStart(addWeeks(value, -1));
    const nextValue = convertToWeekStart(addWeeks(value, +1));
    return (
        <div className="form__cell -months">
            <button className="btn -link -left-arrow -white" onClick={() => onLinkClick(prevValue)}>
                <span className="text">{props.previousLabel}</span>
            </button>
            <label className="form__label _visually-hidden" htmlFor={props.id}>
                {props.label}
            </label>
            <div className="custom-select">
                <select name={props.id} id={props.id} onChange={onChangeWeek} value={getFormattedDate(value)}>
                    {getWeekOptions(undefined, undefined, value).map(value => {
                        const key = getFormattedDate(value);
                        return (
                            <option key={key} value={key}>
                                {getFormattedDay(value)} - {getFormattedDay(convertToWeekEnd(value))}
                            </option>
                        );
                    })}
                </select>
            </div>
            <button className="btn -link -white" onClick={() => onLinkClick(currentValue)}>
                <span className="text">{props.currentLabel}</span>
            </button>
            <button className="btn -link -right-arrow -white" onClick={() => onLinkClick(nextValue)}>
                <span className="text">{props.nextLabel}</span>
            </button>
        </div>
    );
}
